(() => {
  'use strict';
  const root = document.querySelector('[data-bb-privacy-root]');
  if (!root || root.dataset.bbInitialized === 'yes') return;
  root.dataset.bbInitialized = 'yes';
  const messages = JSON.parse(root.querySelector('[data-bb-messages]').textContent);
  const dialog = root.querySelector('dialog');
  const notice = root.querySelector('[data-bb-notice]');
  const noticeError = root.querySelector('[data-bb-notice-error]');
  const outsideStatus = root.querySelector('[data-bb-outside-status]');
  const insideStatus = root.querySelector('[data-bb-inside-status]');
  const fields = [...root.querySelectorAll('[data-bb-category]')];
  const actionButtons = [...root.querySelectorAll('[data-bb-action]')];
  const categories = ['preferences', 'analytics', 'marketing'];
  let api = null, busy = false, returnFocus = null;
  let lastConsent = {preferences: '', analytics: '', marketing: '', sale_of_data: ''};
  function status(message, error = false) {
    if (error) notice.hidden = false;
    outsideStatus.textContent = dialog.open ? '' : message;
    outsideStatus.hidden = dialog.open || !message;
    insideStatus.textContent = dialog.open ? message : '';
    noticeError.textContent = error ? message : '';
    noticeError.hidden = !error;
  }
  function enableActions() {
    actionButtons.forEach(button => { button.disabled = !api || busy; });
    fields.forEach(input => { input.disabled = !api || busy; });
  }
  function read() {
    if (!api) throw new Error('Privacy service unavailable');
    const value = api.currentVisitorConsent();
    if (!value || typeof value !== 'object') throw new Error('Invalid privacy state');
    for (const key of [...categories, 'sale_of_data']) {
      const state = value[key] || '';
      if (!['yes', 'no', ''].includes(state)) throw new Error('Invalid privacy value');
      lastConsent[key] = state;
    }
    return {...lastConsent};
  }
  function displayCurrent() {
    root.querySelector('[data-bb-current]').textContent = messages.current + ' ' + categories.map(key =>
      messages[key] + ': ' + (lastConsent[key] === 'yes' ? messages.yes : lastConsent[key] === 'no' ? messages.no : messages.unset)
    ).join(' · ');
  }
  function syncFields() {
    fields.forEach(input => { input.checked = lastConsent[input.dataset.bbCategory] === 'yes'; });
    displayCurrent();
  }
  function open(trigger) {
    returnFocus = trigger || document.activeElement;
    try { if (api) read(); } catch (_) { api = null; }
    syncFields(); enableActions();
    if (!dialog.open) dialog.showModal();
    status(!api ? messages.unavailable : '');
    root.querySelector('[data-bb-close]').focus();
  }
  function close() { if (dialog.open) dialog.close(); }
  dialog.addEventListener('close', () => {
    insideStatus.textContent = '';
    const destination = returnFocus && returnFocus.isConnected && returnFocus.getClientRects().length
      ? returnFocus : root.querySelector('[data-bb-privacy-open]');
    if (destination && typeof destination.focus === 'function') destination.focus();
  });
  dialog.addEventListener('cancel', () => { /* Native Escape closes and restores focus via close. */ });
  dialog.addEventListener('keydown', event => {
    if (event.key !== 'Tab') return;
    const available = [...dialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')]
      .filter(element => element.getClientRects().length > 0);
    if (!available.length) return;
    const first = available[0], last = available[available.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  });
  root.querySelector('[data-bb-close]').addEventListener('click', close);
  root.querySelector('[data-bb-dismiss]').addEventListener('click', () => { notice.hidden = true; });
  document.addEventListener('click', event => {
    const trigger = event.target.closest('[data-bb-privacy-open]');
    if (trigger) { event.preventDefault(); open(trigger); }
  });
  const pause = ms => new Promise(resolve => setTimeout(resolve, ms));
  async function verify(expected, preserved = null) {
    for (let attempt = 0; attempt < 6; attempt++) {
      const actual = read();
      const matched = Object.entries(expected).every(([key, value]) => actual[key] === (value ? 'yes' : 'no'));
      const unchanged = !preserved || Object.entries(preserved).every(([key, value]) => actual[key] === value);
      if (matched && unchanged) return true;
      if (attempt < 5) await pause(150);
    }
    return false;
  }
  async function save(expected, saleOnly = false) {
    if (!api || busy) return;
    busy = true; enableActions(); status(messages.saving);
    try {
      const before = saleOnly ? read() : null;
      const preserved = before ? Object.fromEntries(categories.map(key => [key, before[key]])) : null;
      await new Promise((resolve, reject) => {
        let completed = false;
        const timer = setTimeout(() => { if (!completed) { completed = true; reject(new Error('Consent callback timeout')); } }, 5000);
        try {
          api.setTrackingConsent(expected, result => {
            if (completed) return;
            completed = true; clearTimeout(timer);
            if (result && result.error) reject(new Error('Consent callback error'));
            else resolve();
          });
        } catch (error) { completed = true; clearTimeout(timer); reject(error); }
      });
      if (!await verify(expected, preserved)) { displayCurrent(); status(messages.mismatch, true); return; }
      syncFields(); notice.hidden = true;
      status(saleOnly ? messages.sale_saved : messages.saved);
    } catch (_) {
      try { read(); displayCurrent(); } catch (_) { /* Keep last confirmed state. */ }
      status(messages.failed, true);
    } finally { busy = false; enableActions(); }
  }
  actionButtons.forEach(button => button.addEventListener('click', () => {
    if (!api || busy) return;
    const action = button.dataset.bbAction;
    if (action === 'sale') { save({sale_of_data: false}, true); return; }
    if (action === 'accept' || action === 'reject') {
      const value = action === 'accept';
      save({preferences: value, analytics: value, marketing: value});
      return;
    }
    if (action === 'save') save(Object.fromEntries(fields.map(input => [input.dataset.bbCategory, input.checked])));
  }));
  function unavailable() {
    api = null; busy = false; enableActions(); notice.hidden = false;
    status(messages.unavailable, true); syncFields();
  }
  let loadFinished = false;
  const loadTimeout = setTimeout(() => { if (!loadFinished) { loadFinished = true; unavailable(); } }, 6000);
  function loaded(error) {
    if (loadFinished) return;
    loadFinished = true; clearTimeout(loadTimeout);
    if (error || !window.Shopify || !window.Shopify.customerPrivacy) { unavailable(); return; }
    api = window.Shopify.customerPrivacy;
    try {
      read(); syncFields(); enableActions();
      notice.hidden = !categories.every(key => lastConsent[key] === '');
      status('');
    } catch (_) { unavailable(); }
  }
  if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') {
    loadFinished = true; clearTimeout(loadTimeout); unavailable();
  } else {
    enableActions();
    try { window.Shopify.loadFeatures([{name: 'consent-tracking-api', version: '0.1'}], loaded); }
    catch (_) { loaded(new Error('Unable to load consent service')); }
  }
})();
